import {
    Action,
    AnyAction,
    EnhancedStore,
    ListenerMiddlewareInstance,
    Middleware,
    PayloadAction,
    Reducer,
    Slice,
    SliceCaseReducers,
    ThunkDispatch,
} from '@reduxjs/toolkit';
import { IContainer, injectContainer } from '../interface/container';

export enum IStoreMiddlewareType {
    Undef = 0,
    Logger = 1,
    SideEffect = 2,
    Other = 3,
    LastValue = 4,
}

export type MiddlewareSideEffect = ListenerMiddlewareInstance<
    unknown,
    ThunkDispatch<unknown, unknown, AnyAction>,
    unknown
>;

export interface IStoreMiddlewareMap {
    [IStoreMiddlewareType.Logger]: Middleware;
    [IStoreMiddlewareType.SideEffect]: MiddlewareSideEffect;
}

export interface IStoreMiddlewares {
    get: <Type extends keyof IStoreMiddlewareMap>(
        type: Type,
    ) => IStoreMiddlewareMap[Type] | undefined;
    add: (middleware: Middleware) => void;
    remove: (middleware: Middleware) => void;
    all: () => Array<Middleware>;
}

export type ReducerUnknown = Reducer<unknown, Action<unknown>>;
export type ReducerActionReset = (state: any, payload: PayloadAction<void>) => void;
export type ReducerSlice = Slice<
    any,
    SliceCaseReducers<any> & { reset: ReducerActionReset },
    string
>;

export interface CallbackEmpty {
    (): void;
}

export interface ReducerManagerEventMap {
    SliceAdded: (slice: ReducerSlice) => void;
    ReducersChanged: CallbackEmpty;
}

export type SubscriberList = {
    [key in keyof ReducerManagerEventMap]: Array<ReducerManagerEventMap[key]>;
};

export interface IReducerRegister {
    getReducerMap: () => Record<string, ReducerUnknown>;
    reduce: (state: any, action: Action<unknown>) => void;
    addSlice: (slice: ReducerSlice) => void;
    add: (key: string, reducer: ReducerUnknown) => void;
    remove: (key: string) => void;
    subscribe: <K extends keyof ReducerManagerEventMap>(
        event: K,
        callback: ReducerManagerEventMap[K],
    ) => void;
    unsubscribe: <K extends keyof ReducerManagerEventMap>(
        event: K,
        callback: ReducerManagerEventMap[K],
    ) => void;
}

export interface IStoresData {
    store: EnhancedStore;
    dispatch: <T extends any>(action: T) => T;
}

export interface IStoresRenderer {
    render: (props: { children?: React.ReactNode }) => JSX.Element;
}

const TYPES = {
    IStoreMiddlewares: Symbol.for('IStoreMiddlewares'),
    IStoresReducer: Symbol.for('IStoresReducer'),
    IStoresData: Symbol.for('IStoresData'),
    IStoresRenderer: Symbol.for('IStoresRenderer'),
};

export function injectStoresMiddleware(
    container: IContainer = injectContainer(),
): IStoreMiddlewares {
    return container.get<IStoreMiddlewares>(TYPES.IStoreMiddlewares);
}

export function bindStoresMiddleware(
    val: IStoreMiddlewares,
    container: IContainer = injectContainer(),
) {
    return container.bind<IStoreMiddlewares>(TYPES.IStoreMiddlewares, val);
}

export function injectStoresData(container: IContainer = injectContainer()): IStoresData {
    return container.get<IStoresData>(TYPES.IStoresData);
}

export function bindStoresReducer(
    val: IReducerRegister,
    container: IContainer = injectContainer(),
) {
    return container.bind<IReducerRegister>(TYPES.IStoresReducer, val);
}

export function injectStoresReducer(container: IContainer = injectContainer()): IReducerRegister {
    return container.get<IReducerRegister>(TYPES.IStoresReducer);
}

export function bindStoresData(val: IStoresData, container: IContainer = injectContainer()) {
    return container.bind<IStoresData>(TYPES.IStoresData, val);
}

export function injectStoresRenderer(container: IContainer = injectContainer()): IStoresRenderer {
    return container.get<IStoresRenderer>(TYPES.IStoresRenderer);
}

export function bindStoresRenderer(
    val: IStoresRenderer,
    container: IContainer = injectContainer(),
) {
    return container.bind<IStoresRenderer>(TYPES.IStoresRenderer, val);
}
